import { ContractLanguageSection } from "components/ContractLanguageSection";
import { IRuleAttachment } from "components/ContractLanguageSection/types";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Chips } from "primereact/chips";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { useNavigate } from "react-router-dom";
import { RulesetApplicability } from "types/DefaultRules/DefaultRules";

import {
  isScopeComplete,
  scopeForApplicability,
} from "../helpers/ruleApplicability";
import { RuleApplicabilityControl } from "./RuleApplicabilityControl";

export interface IExplodingFeeScheduleFormData {
  /** CLE-2842 step 2 — Payer-Provider, Typical or Fallback. */
  applicability: RulesetApplicability;
  providerId: string | null;
  /** undefined = not chosen yet, null = All payers. */
  payerId: number | null | undefined;
  cptCodes: string[];
  totalRateInCents: number | null;
  groupName: string;
  contractLanguage: string;
}

export const INITIAL_FORM_DATA: IExplodingFeeScheduleFormData = {
  applicability: "SCOPED",
  providerId: null,
  payerId: undefined,
  cptCodes: [],
  totalRateInCents: null,
  groupName: "",
  contractLanguage: "",
};

interface IExplodingFeeScheduleFormProps {
  title: string;
  submitLabel: string;
  submitIcon: string;
  formData: IExplodingFeeScheduleFormData;
  onChange: (data: IExplodingFeeScheduleFormData) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  /** Rule id — present in Edit mode, enables attachments. */
  ruleId?: string;
  attachments?: IRuleAttachment[];
  /**
   * Edit mode: the applicability the rule was loaded with. A Typical rule stays Typical; a
   * Payer-Provider rule and a Fallback rule can be converted into each other (CLE-2842 step 2).
   */
  savedApplicability?: RulesetApplicability;
}

export function ExplodingFeeScheduleForm({
  title,
  submitLabel,
  submitIcon,
  formData,
  onChange,
  onSubmit,
  isSubmitting,
  ruleId,
  attachments,
  savedApplicability,
}: IExplodingFeeScheduleFormProps) {
  const navigate = useNavigate();

  const handleInputChange = (
    field: keyof IExplodingFeeScheduleFormData,
    value: any
  ) => {
    onChange({ ...formData, [field]: value });
  };

  const isValid =
    isScopeComplete(formData) &&
    formData.cptCodes.length >= 2 &&
    formData.cptCodes.length <= 15 &&
    formData.totalRateInCents != null &&
    formData.totalRateInCents > 0;

  return (
    <div className="p-4">
      <div className="flex align-items-center mb-4">
        <Button
          icon="pi pi-arrow-left"
          className="p-button-text mr-2"
          onClick={() => navigate("/payer-rules")}
        />
        <h2 className="m-0">{title}</h2>
      </div>

      <Card className="mb-4">
        <RuleApplicabilityControl
          applicability={formData.applicability}
          onApplicabilityChange={(next) =>
            onChange(scopeForApplicability(formData, next))
          }
          providerId={formData.providerId}
          onProviderChange={(providerId) =>
            handleInputChange("providerId", providerId)
          }
          payerId={formData.payerId}
          onPayerChange={(payerId) => handleInputChange("payerId", payerId)}
          savedApplicability={savedApplicability}
        />
        <div className="grid">
          <div className="col-12 md:col-6 field">
            <label htmlFor="groupName">Group Name</label>
            <InputText
              id="groupName"
              className="w-full"
              value={formData.groupName}
              onChange={(e) => handleInputChange("groupName", e.target.value)}
              placeholder="e.g., Orthopedic Bundle"
            />
          </div>
          <div className="col-12 md:col-6 field">
            <label htmlFor="totalRate">Total Rate *</label>
            <InputNumber
              id="totalRate"
              className="w-full"
              value={
                formData.totalRateInCents != null
                  ? formData.totalRateInCents / 100
                  : undefined
              }
              onValueChange={(e) =>
                handleInputChange(
                  "totalRateInCents",
                  e.value != null ? Math.round(e.value * 100) : null
                )
              }
              mode="currency"
              currency="USD"
              locale="en-US"
              min={0.01}
              placeholder="e.g., $1,200.00"
            />
            <small className="text-500">
              Total amount paid across all CPT codes in the ruleset
            </small>
          </div>
          <div className="col-12 field">
            <label htmlFor="cptCodes">CPT Codes * (min 2, max 15)</label>
            <Chips
              id="cptCodes"
              className="w-full"
              value={formData.cptCodes}
              onChange={(e) => handleInputChange("cptCodes", e.value || [])}
              placeholder="96523, J1642, 0272-0372"
              separator=","
            />
            <small className="text-500">
              Press Enter or type comma to add each CPT code.
              {formData.totalRateInCents != null &&
                formData.cptCodes.length >= 2 && (
                  <>
                    {" "}
                    Per-code rate: $
                    {(
                      formData.totalRateInCents /
                      100 /
                      formData.cptCodes.length
                    ).toFixed(2)}
                  </>
                )}
            </small>
          </div>
        </div>
      </Card>

      <ContractLanguageSection
        ruleType="EXPLODING_FEE"
        ruleId={ruleId}
        attachments={attachments}
        value={formData.contractLanguage}
        onChange={(v) => handleInputChange("contractLanguage", v)}
      />

      <div className="flex justify-content-end gap-2">
        <Button
          label="Cancel"
          className="p-button-outlined"
          onClick={() => navigate("/payer-rules")}
        />
        <Button
          label={submitLabel}
          icon={submitIcon}
          disabled={!isValid || isSubmitting}
          onClick={onSubmit}
        />
      </div>
    </div>
  );
}
